import React, { FC, useState, useEffect, useContext } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Button,
  Box,
  Typography,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormControl,
  TextField,
  Alert,
  Chip,
  CircularProgress,
  Backdrop,
} from '@mui/material';
import { DatePicker } from '@mui/lab';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import { Close, Info, CheckCircle } from '@mui/icons-material';
import { format } from 'date-fns';
import { useBoundStore } from 'store';
import { Dialog as DialogEnum } from 'store/types';
import {
  getPublishData,
  getSelectedItemsCount,
  usePartialPublish,
} from 'store/DocumentPublish';
import { LocalizationContext } from 'contexts';
import { documentsClient } from 'clients/documents/documentsClient';

interface PublishDialogProps {
	open: boolean;
	onClose: () => void;
}

interface PublishFormData {
	publishMode: 'update_existing' | 'create_new';
	newPublicationDate?: Date;
}

export const PublishDialog: FC<PublishDialogProps> = ({ open, onClose }) => {
  const { dictionary } = useContext(LocalizationContext);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usedDates, setUsedDates] = useState<Set<string>>(new Set());
  const [isDatesLoading, setIsDatesLoading] = useState(false);

  const toYmd = (d: Date) => format(d, 'yyyy-MM-dd');

  const partialPublish = usePartialPublish();
  const selectedCount = getSelectedItemsCount();


  const dialogOptions = useBoundStore(
    (state) =>
					state.dialogOptions[DialogEnum.PublishDocument] as
							| {
						slug: string;
						currentPublicationDate?: string;
						onPublishSuccess?: () => void;
						document?: any;
					}
							| undefined,
  );

  const {
    slug = '',
    currentPublicationDate = '26-05-2022',
  } = dialogOptions || {};

  const { control, handleSubmit, watch, reset, formState: { errors } } = useForm<PublishFormData>({
    defaultValues: {
      publishMode: 'update_existing',
      newPublicationDate: new Date(),
    },
  });

  const publishMode = watch('publishMode');

  useEffect(() => {
    if (!open || !slug) return;

    setIsDatesLoading(true);
    documentsClient.getVersionHistory(slug)
      .then((res) => {
        const dates = new Set(
							(res.data?.versions ?? [])
							  .map(v => v.published_at?.slice(0, 10))
							  .filter(Boolean) as string[],
        );
        setUsedDates(dates);
      })
      .catch((e) => {
        console.error('Failed to load version history:', e);
      })
      .finally(() => setIsDatesLoading(false));
  }, [open, slug]);


  useEffect(() => {
    setError(null);
  }, [publishMode]);

  useEffect(() => {
    if (!open) {
      setIsSuccess(false);
      setIsPublishing(false);
      setError(null);
    }
  }, [open]);

  const handleClose = () => {
    if (isPublishing) return;

    reset();
    setError(null);
    setIsSuccess(false);
    setIsPublishing(false);
    onClose();
  };

  const newPublicationDate = watch('newPublicationDate');
  const hasDateConflict =
			publishMode === 'create_new' &&
			newPublicationDate instanceof Date &&
			usedDates.has(toYmd(newPublicationDate));


  const onSubmit = async (data: PublishFormData) => {
    setIsPublishing(true);
    setError(null);
    setIsSuccess(false);

    if (data.publishMode === 'create_new' && data.newPublicationDate) {
      const ymd = toYmd(data.newPublicationDate);
      if (usedDates.has(ymd)) {
        setError(dictionary?.publishDialog?.dateAlreadyUsed ?? 'Er bestaat al een versie met deze datum');
        setIsPublishing(false);
        return;
      }
    }

    try {
      const publishData = getPublishData();

      const payload = {
        ...publishData,
        update_existing_version: data.publishMode === 'update_existing',
        new_publication_date:
						data.publishMode === 'create_new' && data.newPublicationDate
						  ? format(data.newPublicationDate, 'yyyy-MM-dd')
						  : undefined,
      };

      await documentsClient.publishDocument(slug, payload);

      setIsSuccess(true);

      setTimeout(() => {
        setIsPublishing(false);
        handleClose();
        window.location.reload();
      }, 500);

    } catch (error: any) {
      if (error.response?.data?.message) {
        setError(error.response.data.message);
      } else if (error.message) {
        setError(error.message);
      } else {
        setError(dictionary.publishDialog.unknownError);
      }
      setIsPublishing(false);
    }
  };

  const getPublishText = () => {
    if (partialPublish) {
      return dictionary.publishDialog.publishSelectedElements(selectedCount);
    }
    return dictionary.publishDialog.publishAllChanges;
  };

  const getRadioText = (mode: 'update' | 'create') => {
    const baseText = partialPublish
      ? dictionary.publishDialog.selectedChanges(selectedCount)
      : dictionary.publishDialog.allChanges;

    if (mode === 'update') {
      return (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 500, color: '#111' }}>
            {dictionary.publishDialog.updateExisting.title(baseText, currentPublicationDate)}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: '#666', mt: 0.5, display: 'block' }}
          >
            {dictionary.publishDialog.updateExisting.subtitle}
          </Typography>
        </Box>
      );
    } else {
      return (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 500, color: '#111' }}>
            {dictionary.publishDialog.createNew.title(baseText)}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: '#666', mt: 0.5, display: 'block' }}
          >
            {dictionary.publishDialog.createNew.subtitle}
          </Typography>
        </Box>
      );
    }
  };


  const renderPublishInfo = () => {
    if (!partialPublish) return null;

    return (
      <Box sx={{ mb: 3 }}>
        <Alert severity="info" icon={<Info/>} sx={{ mb: 2 }}>
          <Typography variant="body2">
            {dictionary.publishDialog.partialPublishActive}
          </Typography>
        </Alert>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {selectedCount > 0 ? (
            <Chip
              label={dictionary.publishDialog.elementsSelected(selectedCount)}
              color="primary"
              variant="outlined"
              size="small"
            />
          ) : (
            <Chip
              label={dictionary.publishDialog.noElementsSelected}
              color="error"
              variant="outlined"
              size="small"
            />
          )}
        </Box>
      </Box>
    );
  };

  const renderLoader = () => (
    <Backdrop
      sx={{
        position: 'absolute',
        zIndex: (theme) => theme.zIndex.drawer + 1,
        backgroundColor: 'rgba(236, 236, 236, 0.9)',
        backdropFilter: 'blur(2px)',
      }}
      open={isPublishing}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
        }}
      >
        {isSuccess ? (
          <>
            <CheckCircle
              sx={{
                fontSize: 64,
                color: '#4caf50',
                animation: 'scaleIn 0.3s ease-out',
                '@keyframes scaleIn': {
                  '0%': {
                    transform: 'scale(0)',
                  },
                  '100%': {
                    transform: 'scale(1)',
                  },
                },
              }}
            />
            <Typography
              variant="h6"
              sx={{
                color: '#111',
                fontWeight: 600,
                textAlign: 'center',
              }}
            >
              {dictionary.publishDialog.publishSuccessful}
            </Typography>
          </>
        ) : (
          <>
            <CircularProgress
              size={64}
              thickness={4}
              sx={{
                color: '#163871',
                animation: 'pulse 2s infinite',
                '@keyframes pulse': {
                  '0%': {
                    transform: 'scale(1)',
                    opacity: 1,
                  },
                  '50%': {
                    transform: 'scale(1.1)',
                    opacity: 0.8,
                  },
                  '100%': {
                    transform: 'scale(1)',
                    opacity: 1,
                  },
                },
              }}
            />
            <Typography
              variant="h6"
              sx={{
                color: '#111',
                fontWeight: 600,
                textAlign: 'center',
              }}
            >
              {dictionary.publishDialog.publishing}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: '#666',
                textAlign: 'center',
              }}
            >
              {dictionary.publishDialog.processingChanges}
            </Typography>
          </>
        )}
      </Box>
    </Backdrop>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: '#ececec',
          color: '#111',
          position: 'relative',
          overflow: 'hidden',
        },
      }}
    >
      {renderLoader()}

      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
          color: '#111',
          fontWeight: 600,
          opacity: isPublishing ? 0.5 : 1,
          transition: 'opacity 0.3s ease',
        }}
      >
        {dictionary.publishDialog.title}
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{
            color: '#111',
            opacity: isPublishing ? 0.3 : 1,
            pointerEvents: isPublishing ? 'none' : 'auto',
          }}
          disabled={isPublishing}
        >
          <Close/>
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          pt: 2,
          opacity: isPublishing ? 0.5 : 1,
          transition: 'opacity 0.3s ease',
        }}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Box mb={3}>
            <Typography variant="body1" sx={{ mb: 2, color: '#111' }}>
              {getPublishText()}
            </Typography>

            {renderPublishInfo()}

            <Typography
              variant="body2"
              sx={{ mb: 3, color: '#111', fontWeight: 500 }}
            >
              {dictionary.publishDialog.makeChoice}
            </Typography>

            <FormControl component="fieldset" error={!!errors.publishMode} disabled={isPublishing}>
              <Controller
                name="publishMode"
                control={control}
                rules={{ required: dictionary.publishDialog.selectPublicationMode }}
                render={({ field }) => (
                  <RadioGroup {...field}>
                    <FormControlLabel
                      value="update_existing"
                      control={
                        <Radio
                          sx={{
                            color: '#163871',
                            '&.Mui-checked': { color: '#163871' },
                          }}
                          disabled={isPublishing}
                        />
                      }
                      label={getRadioText('update')}
                      sx={{
                        alignItems: 'flex-start',
                        mb: 2,
                        '& .MuiFormControlLabel-label': { mt: -0.5 },
                      }}
                      disabled={isPublishing}
                    />

                    <FormControlLabel
                      value="create_new"
                      control={
                        <Radio
                          sx={{
                            color: '#163871',
                            '&.Mui-checked': { color: '#163871' },
                          }}
                          disabled={isPublishing}
                        />
                      }
                      label={getRadioText('create')}
                      sx={{
                        alignItems: 'flex-start',
                        mb: 2,
                        '& .MuiFormControlLabel-label': { mt: -0.5 },
                      }}
                      disabled={isPublishing}
                    />
                  </RadioGroup>
                )}
              />
            </FormControl>

            {publishMode === 'create_new' && (
              <Box mt={2} ml={4}>
                <Typography variant="body2" sx={{ mb: 1, color: '#666' }}>
                  {dictionary.publishDialog.selectNewPublicationDate}
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <Controller
                    name="newPublicationDate"
                    control={control}
                    rules={{
                      required: publishMode === 'create_new'
                        ? (dictionary?.publishDialog?.dateRequiredForNewVersion ?? 'Datum is verplicht voor nieuwe versie')
                        : false,
                      validate: (value) => {
                        if (publishMode !== 'create_new' || !value) return true;
                        try {
                          const ymd = toYmd(value as Date);
                          return usedDates.has(ymd)
                            ? (dictionary?.publishDialog?.dateAlreadyUsed ?? 'Er bestaat al een versie mit deze datum')
                            : true;
                        } catch {
                          return true;
                        }
                      },
                    }}
                    render={({ field }) => (
                      <DatePicker
                        {...field}
                        label={dictionary.publishDialog.publicationDate}
                        inputFormat="dd-MM-yyyy"
                        disabled={isPublishing || isDatesLoading}
                        shouldDisableDate={(date) => !!date && usedDates.has(toYmd(date as Date))}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            error={!!errors.newPublicationDate}
                            helperText={errors.newPublicationDate?.message}
                            disabled={isPublishing || isDatesLoading}
                            inputProps={{ ...params.inputProps, readOnly: true }}
                            sx={{
                              '& .MuiInputBase-root': { backgroundColor: '#fff', cursor: 'pointer' },
                              '& .MuiInputBase-input': { cursor: 'pointer' },
                              '& .MuiInputLabel-root': { color: '#111' },
                              '& .MuiOutlinedInput-root': {
                                '& fieldset': { borderColor: '#ccc' },
                                '&:hover fieldset': { borderColor: '#163871' },
                                '&.Mui-focused fieldset': { borderColor: '#163871' },
                              },
                            }}
                          />
                        )}
                      />
                    )}
                  />
                </LocalizationProvider>
              </Box>
            )}

          </Box>

          <Box
            display="flex"
            justifyContent="flex-end"
            gap={2}
            pt={2}
            borderTop="1px solid #ccc"
          >
            <Button
              variant="outlined"
              onClick={handleClose}
              disabled={isPublishing}
              sx={{
                color: '#111',
                borderColor: '#ccc',
                '&:hover': {
                  borderColor: '#163871',
                  backgroundColor: 'rgba(22, 56, 113, 0.04)',
                },
                '&:disabled': {
                  opacity: 0.5,
                },
              }}
            >
              {dictionary.publishDialog.cancel}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={
                isPublishing ||
											isDatesLoading ||
											(partialPublish && selectedCount === 0) ||
											hasDateConflict
              }
              sx={{
                backgroundColor: '#163871',
                '&:hover': {
                  backgroundColor: '#0f2a5a',
                },
                '&:disabled': {
                  backgroundColor: '#ccc',
                },
                minWidth: '120px',
              }}
            >
              {isPublishing ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} sx={{ color: 'white' }}/>
                  {dictionary.publishDialog.publishing}
                </Box>
              ) : (
                dictionary.publishDialog.publish
              )}
            </Button>
          </Box>
        </form>
      </DialogContent>
    </Dialog>
  );
};